import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import subscriptionService from '../../utils/services/subscriptionService';
import authService from '../../utils/services/authService';
import snipeLogo from '../../assets/snipe_logo.png';
// logoIcon removed - iPhone mockup replaced with hero image
// import logoIcon from '../../assets/logo_icon.png';
import heroImage from '../../assets/hero_image.png';
import sportsbetLogo from '../../assets/sb.png';
import tabLogo from '../../assets/tab.jpg';
import pointsbetLogo from '../../assets/pb.png';
import dabbleLogo from '../../assets/dabble.webp';
import nedsLogo from '../../assets/neds.jpg';
import betrightLogo from '../../assets/betright.jpg';
import tabtouchLogo from '../../assets/tabtouch.webp';
// fanduelLogo and draftkingsLogo removed from carousel per redesign
// import fanduelLogo from '../../assets/fanduel.jpg';
// import draftkingsLogo from '../../assets/draftkings.jpg';
import aflLogo from '../../assets/afl_logo.png';
import nbaLogo from '../../assets/nba_logo.png';
// Screenshot images removed in strategies redesign
// import positiveEvImage from '../../assets/positive_ev.png';
// import marketValueImage from '../../assets/market_value.png';
// import arbsImage from '../../assets/arbs.png';
import trackerOne from '../../assets/tracker_one.png';
import trackerTwo from '../../assets/tracker_two.png';
import trackerThree from '../../assets/tracker_three.png';
import trackerFour from '../../assets/tracker_four.png';
import LoginModal from '../Login/LoginModal';
import RegisterModal from '../Register/RegisterModal';

function LandingPage() {
    const observerRef = useRef(null);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
    const [selectedPlanForRegister, setSelectedPlanForRegister] = useState(null);
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [isLoadingPlans, setIsLoadingPlans] = useState(true);
    const [selectedSport, setSelectedSport] = useState('NBA');
    const [currentMobilePlanIndex, setCurrentMobilePlanIndex] = useState(0);
    const [currentStrategyIndex, setCurrentStrategyIndex] = useState(0);
    const [currentTrackerIndex, setCurrentTrackerIndex] = useState(0);
    const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
    const trackerScrollRef = useRef(null);
    const mobileScrollRef = useRef(null);
    const [expandedFaqItems, setExpandedFaqItems] = useState([]);
    const [registrationEmail, setRegistrationEmail] = useState('');
    const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
    const [isFreeTierRegistration, setIsFreeTierRegistration] = useState(false);
    const [isProcessingRegistration, setIsProcessingRegistration] = useState(false);
    const [showSessionExpiredBanner, setShowSessionExpiredBanner] = useState(false);
    // Alert animation state removed - iPhone mockup replaced with hero image
    // const [visibleAlerts, setVisibleAlerts] = useState([0]);
    // const [alertAnimationKey, setAlertAnimationKey] = useState(0);
    // const [nextAlertIndex, setNextAlertIndex] = useState(1);
    // selectedChannel state removed - strategies section no longer uses tabs
    const { isAuthenticated, isLoading, user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Sample alerts data removed - iPhone mockup replaced with hero image
    // const sampleAlerts = [...];

    // Toggle FAQ item expansion
    const toggleFaqItem = (index) => {
        setExpandedFaqItems(prev => 
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    // Check for session expiration on mount
    useEffect(() => {
        const sessionExpired = sessionStorage.getItem('sessionExpired');
        if (sessionExpired === 'true') {
            setShowSessionExpiredBanner(true);
            sessionStorage.removeItem('sessionExpired');
            
            // Auto-hide banner after 10 seconds
            setTimeout(() => {
                setShowSessionExpiredBanner(false);
            }, 10000);
        }
    }, []);

    // Alert animation effect removed - iPhone mockup replaced with hero image

    // Redirect to dashboard if user is already authenticated
    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            navigate(user?.discordLinked ? '/odds-screen' : '/discord');
        }
    }, [isAuthenticated, isLoading, navigate, user]);

    // Handle registration success/cancel from Stripe redirect
    useEffect(() => {
        const registrationSuccess = searchParams.get('registration_success');
        const registrationCancelled = searchParams.get('registration_cancelled');
        const sessionId = searchParams.get('session_id');

        if (registrationSuccess === 'true' && sessionId) {
            handleRegistrationSuccess(sessionId);
        }

        if (registrationCancelled === 'true') {
            // Clean up URL silently
            setSearchParams({});
        }
    }, [searchParams, setSearchParams]);

    // Handle successful registration - fetch email and auto-login or show login modal
    const handleRegistrationSuccess = async (sessionId) => {
        setIsProcessingRegistration(true);
        
        try {
            // Fetch registration info (includes token if account was created)
            const result = await authService.getRegistrationInfo(sessionId);
            
            if (result.success && result.data) {
                const { email, auto_login, token, status, message } = result.data;
                
                // Check if we can auto-login
                if (auto_login && token) {
                    // Store token and show success
                    authService.setToken(token);
                    setShowRegistrationSuccess(true);
                    
                    // Decode token and update auth context
                    const payload = authService.decodeToken();
                    if (payload) {
                        // Give time for success animation, then redirect
                        setTimeout(() => {
                            setShowRegistrationSuccess(false);
                            // Navigate to dashboard (AuthContext will handle the rest)
                            window.location.href = '/discord';
                        }, 2000);
                    }
                } else if (status === 'paid' && !auto_login) {
                    // Payment complete but account still processing
                    setShowRegistrationSuccess(true);
                    
                    // Show processing message and retry after delay
                    setTimeout(async () => {
                        // Retry getting registration info
                        const retryResult = await authService.getRegistrationInfo(sessionId);
                        
                        if (retryResult.success && retryResult.data.auto_login && retryResult.data.token) {
                            // Account created, auto-login
                            authService.setToken(retryResult.data.token);
                            setTimeout(() => {
                                setShowRegistrationSuccess(false);
                                window.location.href = '/discord';
                            }, 1000);
                        } else {
                            // Still processing, show login modal
                            setShowRegistrationSuccess(false);
                            setRegistrationEmail(email);
                            setIsLoginModalOpen(true);
                        }
                    }, 3000);
                } else {
                    // Payment not completed or other issue, show login modal
                    setRegistrationEmail(email);
                    setShowRegistrationSuccess(true);
                    setTimeout(() => {
                        setShowRegistrationSuccess(false);
                        setIsLoginModalOpen(true);
                    }, 2000);
                }
            } else {
                // Failed to get registration info, show login modal
                setShowRegistrationSuccess(true);
                setTimeout(() => {
                    setShowRegistrationSuccess(false);
                    setIsLoginModalOpen(true);
                }, 2000);
            }
            
            // Clean up URL
            setSearchParams({});
        } catch (error) {
            console.error('Failed to fetch registration info:', error);
            // Error fetching info, show login modal
            setShowRegistrationSuccess(true);
            setTimeout(() => {
                setShowRegistrationSuccess(false);
                setIsLoginModalOpen(true);
            }, 2000);
            setSearchParams({});
        } finally {
            setIsProcessingRegistration(false);
        }
    };

    // Fetch subscription plans (public, no auth required)
    useEffect(() => {
        const fetchPlans = async () => {
            setIsLoadingPlans(true);
            const result = await subscriptionService.getAllPlans();
            
            if (result.success) {
                setSubscriptionPlans(result.data);
            }
            setIsLoadingPlans(false);
        };

        fetchPlans();
    }, []);

    // Reset mobile plan index when sport changes and default to PRO, scroll to it
    const initialScrollDone = useRef(false);
    useEffect(() => {
        const selectedSportPlans = subscriptionPlans.find(s => s.sport === selectedSport);
        if (selectedSportPlans?.plans) {
            const visiblePlans = selectedSportPlans.plans.filter(plan => plan.show !== false);
            const proIndex = visiblePlans.findIndex(plan => plan.tier === 'PRO');
            const targetIndex = proIndex !== -1 ? proIndex : 0;
            setCurrentMobilePlanIndex(targetIndex);

            const scrollToCard = () => {
                if (mobileScrollRef.current) {
                    const scrollContainer = mobileScrollRef.current;
                    const firstCard = scrollContainer.children[0];
                    if (firstCard) {
                        // Use actual card width + gap (16px = gap-4) for accurate positioning
                        const cardWidth = firstCard.offsetWidth + 16;
                        const isInitial = !initialScrollDone.current;
                        scrollContainer.scrollTo({ left: targetIndex * cardWidth, behavior: isInitial ? 'instant' : 'smooth' });
                        initialScrollDone.current = true;
                    }
                }
            };
            // Delay to ensure DOM is rendered
            setTimeout(scrollToCard, 150);
        }
    }, [selectedSport, subscriptionPlans]);

    useEffect(() => {
        // Intersection Observer for scroll animations
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animate-in');
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -100px 0px'
            }
        );

        // Observe all sections with scroll-animate class
        const elements = document.querySelectorAll('.scroll-animate');
        elements.forEach((el) => {
            if (observerRef.current) {
                observerRef.current.observe(el);
            }
        });

        // Fallback for in-app browsers (Instagram, Facebook) where Intersection Observer may not work
        // Force all animated elements to be visible after 1.5 seconds if they haven't been animated
        const fallbackTimeout = setTimeout(() => {
            const animatedElements = document.querySelectorAll('.scroll-animate');
            animatedElements.forEach((el) => {
                if (!el.classList.contains('animate-in')) {
                    el.classList.add('animate-in');
                }
            });
        }, 1500);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            clearTimeout(fallbackTimeout);
        };
    }, []);

    return (
        <div className="min-h-screen bg-[#020817] text-white relative overflow-x-hidden">
            {/* Gradient Background - single refined radial glow */}
            <div className="fixed inset-0 z-0 bg-[#020817]">
                <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-emerald-500/[0.07] rounded-full blur-[120px]"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-teal-600/[0.04] rounded-full blur-[100px]"></div>
            </div>
            
            {/* Content Wrapper */}
            <div className="relative z-10 overflow-x-hidden w-full">
            {/* Session Expired Banner */}
            {showSessionExpiredBanner && (
                <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4 animate-slideDown">
                    <div className="bg-red-950/90 backdrop-blur-xl border border-red-500/40 rounded-xl shadow-2xl p-4">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                                <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-white font-semibold text-sm mb-0.5">Session Expired</h3>
                                <p className="text-red-200/80 text-xs">Your session has expired. Please login again.</p>
                            </div>
                            <button 
                                onClick={() => setShowSessionExpiredBanner(false)}
                                className="flex-shrink-0 text-red-300/60 hover:text-white transition-colors duration-200"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Navigation - sticky transparent */}
            <nav className="sticky top-0 z-40 bg-transparent backdrop-blur-md">
                <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-6 sm:space-x-8">
                        <img src={snipeLogo} alt="Snipe Logo" className="h-14 sm:h-16 w-auto object-contain" />
                        
                        <div className="hidden lg:flex items-center space-x-6">
                            <a href="#how-it-works" className="text-slate-300 hover:text-white transition-colors duration-200 text-base font-medium">Getting started</a>
                            <a href="#strategies" className="text-slate-300 hover:text-white transition-colors duration-200 text-base font-medium">How it works</a>
                            <a href="#results-tracker" className="text-slate-300 hover:text-white transition-colors duration-200 text-base font-medium">Bet Tracker</a>
                            <a href="#pricing" className="text-slate-300 hover:text-white transition-colors duration-200 text-base font-medium">Pricing</a>
                            <a href="#faq" className="text-slate-300 hover:text-white transition-colors duration-200 text-base font-medium">FAQ</a>
                        </div>
                    </div>

                    <button 
                        onClick={() => setIsLoginModalOpen(true)}
                        className="bg-emerald-500 hover:bg-emerald-400 text-black px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 shadow-[0_0_15px_rgba(16,185,129,0.2)]"
                    >
                        Login
                    </button>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="w-full overflow-x-hidden scroll-animate">
                <div className="container mx-auto px-4 sm:px-6 pt-16 md:pt-20 pb-8 md:pb-12">
                    {/* Side-by-side layout: text left, image right */}
                    <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
                        {/* Left - Text Content */}
                        <div className="flex-1 space-y-6 text-center lg:text-left">
                            {/* Heading */}
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight">
                                The edge you need
                                <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300">to beat the bookies</span>
                            </h1>

                            {/* Subheading */}
                            <p className="text-slate-400 text-base sm:text-lg max-w-lg mx-auto lg:mx-0 leading-relaxed">
                                Providing punters with the tools necessary to get an edge on the sportsbooks over the long term.
                            </p>

                            {/* CTA Buttons */}
                            <div className="flex flex-col sm:flex-row items-center lg:items-start gap-3 pt-2">
                                <button 
                                    onClick={() => {
                                        const nbaSport = subscriptionPlans.find(s => s.sport === 'NBA');
                                        const basicPlan = nbaSport?.plans?.find(p => p.tier === 'BASIC' && p.show !== false);
                                        if (basicPlan) {
                                            setSelectedPlanForRegister({ ...basicPlan, sport: 'NBA' });
                                            setIsRegisterModalOpen(true);
                                        }
                                    }}
                                    className="group bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-8 py-3.5 rounded-xl text-sm sm:text-base transition-all duration-200 shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:shadow-[0_0_30px_rgba(16,185,129,0.4)]"
                                >
                                    Start free 7-day trial
                                    <svg className="inline-block w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                </button>
                                <a 
                                    href="#pricing"
                                    className="text-slate-400 hover:text-white text-sm font-medium transition-colors duration-200 flex items-center gap-1.5 py-3.5 px-4"
                                >
                                    View pricing
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </a>
                            </div>

                            {/* Social proof / stats row */}
                            <div className="flex items-center justify-center lg:justify-start gap-6 pt-4">
                                <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-slate-400 text-xs">EV & Arb alerts</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-slate-400 text-xs">Built-in bet tracker</span>
                                </div>
                                <div className="hidden sm:flex items-center gap-2">
                                    <svg className="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-slate-400 text-xs">24/7 monitoring</span>
                                </div>
                            </div>
                        </div>

                        {/* Right - Hero Image */}
                        <div className="flex-1 relative w-full lg:w-auto">
                            <div className="relative">
                                {/* Subtle glow behind image */}
                                <div className="absolute -inset-6 bg-emerald-500/[0.06] rounded-3xl blur-3xl"></div>
                                <img 
                                    src={heroImage} 
                                    alt="Snipe application on MacBook and iPhone" 
                                    className="relative w-full h-auto object-contain drop-shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Bookmaker Carousel */}
                    <div className="pt-12 md:pt-16 w-full max-w-3xl mx-auto">
                        <p className="text-slate-600 text-[11px] uppercase tracking-[0.2em] mb-5 text-center font-medium">Supported Bookmakers</p>
                        <div className="relative overflow-hidden w-full">
                            <div className="hidden sm:block absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#020817] to-transparent z-10"></div>
                            <div className="hidden sm:block absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#020817] to-transparent z-10"></div>
                            <div className="flex animate-scroll py-2">
                                <div className="flex items-center gap-5 sm:gap-8 opacity-40 hover:opacity-60 transition-opacity duration-500">
                                    <img src={sportsbetLogo} alt="Sportsbet" className="w-10 h-10 min-w-[40px] sm:w-14 sm:h-14 sm:min-w-[56px] rounded-full object-cover" />
                                    <img src={tabLogo} alt="TAB" className="w-10 h-10 min-w-[40px] sm:w-14 sm:h-14 sm:min-w-[56px] rounded-full object-cover" />
                                    <img src={pointsbetLogo} alt="Pointsbet" className="w-10 h-10 min-w-[40px] sm:w-14 sm:h-14 sm:min-w-[56px] rounded-full object-cover" />
                                    <img src={dabbleLogo} alt="Dabble" className="w-10 h-10 min-w-[40px] sm:w-14 sm:h-14 sm:min-w-[56px] rounded-full object-cover" />
                                    <img src={nedsLogo} alt="Neds" className="w-10 h-10 min-w-[40px] sm:w-14 sm:h-14 sm:min-w-[56px] rounded-full object-cover" />
                                    <img src={betrightLogo} alt="Betright" className="w-10 h-10 min-w-[40px] sm:w-14 sm:h-14 sm:min-w-[56px] rounded-full object-cover" />
                                    <img src={tabtouchLogo} alt="Tabtouch" className="w-10 h-10 min-w-[40px] sm:w-14 sm:h-14 sm:min-w-[56px] rounded-full object-cover mr-5 sm:mr-8" />
                                </div>
                                <div className="flex items-center gap-5 sm:gap-8 opacity-40 hover:opacity-60 transition-opacity duration-500">
                                    <img src={sportsbetLogo} alt="Sportsbet" className="w-10 h-10 min-w-[40px] sm:w-14 sm:h-14 sm:min-w-[56px] rounded-full object-cover" />
                                    <img src={tabLogo} alt="TAB" className="w-10 h-10 min-w-[40px] sm:w-14 sm:h-14 sm:min-w-[56px] rounded-full object-cover" />
                                    <img src={pointsbetLogo} alt="Pointsbet" className="w-10 h-10 min-w-[40px] sm:w-14 sm:h-14 sm:min-w-[56px] rounded-full object-cover" />
                                    <img src={dabbleLogo} alt="Dabble" className="w-10 h-10 min-w-[40px] sm:w-14 sm:h-14 sm:min-w-[56px] rounded-full object-cover" />
                                    <img src={nedsLogo} alt="Neds" className="w-10 h-10 min-w-[40px] sm:w-14 sm:h-14 sm:min-w-[56px] rounded-full object-cover" />
                                    <img src={betrightLogo} alt="Betright" className="w-10 h-10 min-w-[40px] sm:w-14 sm:h-14 sm:min-w-[56px] rounded-full object-cover" />
                                    <img src={tabtouchLogo} alt="Tabtouch" className="w-10 h-10 min-w-[40px] sm:w-14 sm:h-14 sm:min-w-[56px] rounded-full object-cover" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Getting Started Section */}
            <div id="how-it-works" className="container mx-auto px-6 py-8 md:py-10 scroll-animate">
                <div className="text-center mb-12 md:mb-16">
                    <p className="text-emerald-400 text-xs uppercase tracking-[0.2em] font-medium mb-3">How it works</p>
                    <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
                        Get started in minutes
                    </h2>
                </div>

                {/* Timeline Steps */}
                <div className="max-w-4xl mx-auto">
                    <div className="grid grid-cols-3 gap-0 relative">
                        {/* Connecting line - desktop (vertically centered on the 48px circles = top-6) */}
                        <div className="hidden md:block absolute top-6 left-[16.67%] right-[16.67%] h-px bg-gradient-to-r from-emerald-500/40 via-emerald-500/20 to-emerald-500/40"></div>
                        {/* Connecting line - mobile (vertically centered on the 40px circles = top-5) */}
                        <div className="md:hidden absolute top-5 left-[16.67%] right-[16.67%] h-px bg-gradient-to-r from-emerald-500/40 via-emerald-500/20 to-emerald-500/40"></div>

                        {/* Step 1 */}
                        <div className="relative flex flex-col items-center gap-0 px-2 md:px-4">
                            <div className="relative z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#0a1628] border border-emerald-500/30 flex items-center justify-center shrink-0 mb-4 md:mb-6">
                                <span className="text-emerald-400 font-semibold text-sm">1</span>
                            </div>
                            <div className="text-center">
                                <h3 className="text-base md:text-lg font-semibold mb-1.5">Create Account</h3>
                                <p className="text-xs md:text-sm text-slate-400 leading-relaxed">
                                    Sign up with your email and choose a plan that fits your needs.
                                </p>
                            </div>
                        </div>

                        {/* Step 2 */}
                        <div className="relative flex flex-col items-center gap-0 px-2 md:px-4">
                            <div className="relative z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#0a1628] border border-emerald-500/30 flex items-center justify-center shrink-0 mb-4 md:mb-6">
                                <span className="text-emerald-400 font-semibold text-sm">2</span>
                            </div>
                            <div className="text-center">
                                <h3 className="text-base md:text-lg font-semibold mb-1.5">Link Discord</h3>
                                <p className="text-xs md:text-sm text-slate-400 leading-relaxed">
                                    Connect your Discord to access our EV/Arb bots and community.
                                </p>
                            </div>
                        </div>

                        {/* Step 3 */}
                        <div className="relative flex flex-col items-center gap-0 px-2 md:px-4">
                            <div className="relative z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#0a1628] border border-emerald-500/30 flex items-center justify-center shrink-0 mb-4 md:mb-6">
                                <span className="text-emerald-400 font-semibold text-sm">3</span>
                            </div>
                            <div className="text-center">
                                <h3 className="text-base md:text-lg font-semibold mb-1.5">Receive Alerts</h3>
                                <p className="text-xs md:text-sm text-slate-400 leading-relaxed">
                                    Get EV, Arbitrage, and Value bet alerts delivered 24/7.
                                </p>
                                </div>
                        </div>
                    </div>
                </div>

                {/* CTA */}
                <div className="text-center mt-12">
                    <button 
                        onClick={() => {
                            const nbaSport = subscriptionPlans.find(s => s.sport === 'NBA');
                            const basicPlan = nbaSport?.plans?.find(p => p.tier === 'BASIC' && p.show !== false);
                            if (basicPlan) {
                                setSelectedPlanForRegister({ ...basicPlan, sport: 'NBA' });
                                setIsRegisterModalOpen(true);
                            }
                        }}
                        className="text-emerald-400 hover:text-emerald-300 text-sm font-medium transition-colors duration-200 group"
                    >
                        Start your free trial
                        <svg className="inline-block w-4 h-4 ml-1 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Strategies Section */}
            <div id="strategies" className="container mx-auto px-6 py-8 md:py-10 scroll-animate">
                <div className="text-center mb-14">
                    <p className="text-emerald-400 text-xs uppercase tracking-[0.2em] font-medium mb-3">Our strategies</p>
                    <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
                        Three proven edges,<br className="hidden sm:block" /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300">one platform</span>
                    </h2>
                    <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto">
                        Our bots scan hundreds of markets across multiple bookmakers with lightning speed, identifying profitable opportunities before they disappear.
                    </p>
                </div>

                {/* Strategy Cards - Desktop Grid */}
                <div className="hidden md:grid md:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto">
                    {/* Positive EV */}
                    <div className="group rounded-2xl p-6 md:p-7 border border-white/[0.04] bg-white/[0.02] hover:border-emerald-500/20 hover:bg-emerald-500/[0.03] transition-all duration-300 text-center">
                        <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-5 mx-auto">
                            <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold mb-2">Positive EV</h3>
                        <p className="text-slate-400 text-sm leading-relaxed mb-5">
                            Identifies bets where the true probability of winning is higher than what the bookmaker's odds suggest, giving you a mathematical edge over time.
                        </p>
                        <div className="inline-flex flex-col items-start space-y-1.5 pt-4 border-t border-white/[0.04] w-full">
                            {['BASIC', 'PRO', 'ELITE'].map((tier) => (
                                <div key={tier} className="flex items-center gap-2 text-xs text-slate-500 mx-auto">
                                    <svg className="w-3.5 h-3.5 text-emerald-500/60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                    <span><span className="text-emerald-400 font-medium">{tier}</span> Tier</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* Market Value */}
                    <div className="group rounded-2xl p-6 md:p-7 border border-white/[0.04] bg-white/[0.02] hover:border-emerald-500/20 hover:bg-emerald-500/[0.03] transition-all duration-300 text-center">
                        <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-5 mx-auto">
                            <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold mb-2">Market Value</h3>
                        <p className="text-slate-400 text-sm leading-relaxed mb-5">
                            Tracks odds movements across bookmakers to establish market consensus and alerts you when a bookmaker's odds are out of line, creating a potential edge.
                        </p>
                        <div className="space-y-1.5 pt-4 border-t border-white/[0.04]">
                            <div className="flex items-center gap-2 text-xs text-slate-500 justify-center">
                                <svg className="w-3.5 h-3.5 text-emerald-500/60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                <span>11-19% Edge in <span className="text-emerald-400 font-medium">PRO</span></span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-500 justify-center">
                                <svg className="w-3.5 h-3.5 text-emerald-500/60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                <span>20%+ Edge in <span className="text-emerald-400 font-medium">ELITE</span></span>
                            </div>
                        </div>
                        <p className="text-[11px] text-slate-600 italic mt-3">
                            Not the same as Positive EV — true fair price can't be calculated for one-sided player props.
                        </p>
                    </div>
                    {/* Arbitrage */}
                    <div className="group rounded-2xl p-6 md:p-7 border border-white/[0.04] bg-white/[0.02] hover:border-emerald-500/20 hover:bg-emerald-500/[0.03] transition-all duration-300 text-center">
                        <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-5 mx-auto">
                            <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold mb-2">Arbitrage</h3>
                        <p className="text-slate-400 text-sm leading-relaxed mb-5">
                            Risk-free strategy that guarantees profit by betting on all outcomes across different bookmakers at odds that ensure a positive return regardless of result.
                        </p>
                        <div className="space-y-1.5 pt-4 border-t border-white/[0.04]">
                            {[
                                { tier: 'BASIC', range: '3-5%' },
                                { tier: 'PRO', range: '5-15%' },
                                { tier: 'ELITE', range: '15%+' }
                            ].map((item) => (
                                <div key={item.tier} className="flex items-center gap-2 text-xs text-slate-500 justify-center">
                                    <svg className="w-3.5 h-3.5 text-emerald-500/60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                    <span>{item.range} Arb in <span className="text-emerald-400 font-medium">{item.tier}</span></span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Strategy Cards - Mobile Swipeable Carousel */}
                <div className="md:hidden">
                    <div 
                        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 px-4 no-scrollbar" 
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                        onScroll={(e) => {
                            const scrollContainer = e.target;
                            const firstCard = scrollContainer.children[0];
                            if (firstCard) {
                                const cardWidth = firstCard.offsetWidth + 16;
                                const newIndex = Math.round(scrollContainer.scrollLeft / cardWidth);
                                setCurrentStrategyIndex(Math.min(newIndex, 2));
                            }
                        }}
                    >
                        {/* Positive EV */}
                        <div className="flex-shrink-0 w-[85vw] max-w-[340px] snap-center group rounded-2xl p-6 border border-white/[0.04] bg-white/[0.02] text-center">
                            <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-5 mx-auto">
                                <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold mb-2">Positive EV</h3>
                            <p className="text-slate-400 text-sm leading-relaxed mb-5">
                                Identifies bets where the true probability of winning is higher than what the bookmaker's odds suggest, giving you a mathematical edge over time.
                            </p>
                            <div className="inline-flex flex-col items-start space-y-1.5 pt-4 border-t border-white/[0.04] w-full">
                                {['BASIC', 'PRO', 'ELITE'].map((tier) => (
                                    <div key={tier} className="flex items-center gap-2 text-xs text-slate-500 mx-auto">
                                        <svg className="w-3.5 h-3.5 text-emerald-500/60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                        <span><span className="text-emerald-400 font-medium">{tier}</span> Tier</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* Market Value */}
                        <div className="flex-shrink-0 w-[85vw] max-w-[340px] snap-center group rounded-2xl p-6 border border-white/[0.04] bg-white/[0.02] text-center">
                            <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-5 mx-auto">
                                <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold mb-2">Market Value</h3>
                            <p className="text-slate-400 text-sm leading-relaxed mb-5">
                                Tracks odds movements across bookmakers to establish market consensus and alerts you when a bookmaker's odds are out of line, creating a potential edge.
                            </p>
                            <div className="space-y-1.5 pt-4 border-t border-white/[0.04]">
                                <div className="flex items-center gap-2 text-xs text-slate-500 justify-center">
                                    <svg className="w-3.5 h-3.5 text-emerald-500/60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                    <span>11-19% Edge in <span className="text-emerald-400 font-medium">PRO</span></span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-500 justify-center">
                                    <svg className="w-3.5 h-3.5 text-emerald-500/60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                    <span>20%+ Edge in <span className="text-emerald-400 font-medium">ELITE</span></span>
                                </div>
                            </div>
                            <p className="text-[11px] text-slate-600 italic mt-3">
                                Not the same as Positive EV — true fair price can't be calculated for one-sided player props.
                            </p>
                        </div>
                        {/* Arbitrage */}
                        <div className="flex-shrink-0 w-[85vw] max-w-[340px] snap-center group rounded-2xl p-6 border border-white/[0.04] bg-white/[0.02] text-center">
                            <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-5 mx-auto">
                                <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold mb-2">Arbitrage</h3>
                            <p className="text-slate-400 text-sm leading-relaxed mb-5">
                                Risk-free strategy that guarantees profit by betting on all outcomes across different bookmakers at odds that ensure a positive return regardless of result.
                            </p>
                            <div className="space-y-1.5 pt-4 border-t border-white/[0.04]">
                                {[
                                    { tier: 'BASIC', range: '3-5%' },
                                    { tier: 'PRO', range: '5-15%' },
                                    { tier: 'ELITE', range: '15%+' }
                                ].map((item) => (
                                    <div key={item.tier} className="flex items-center gap-2 text-xs text-slate-500 justify-center">
                                        <svg className="w-3.5 h-3.5 text-emerald-500/60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                        <span>{item.range} Arb in <span className="text-emerald-400 font-medium">{item.tier}</span></span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    {/* Dot indicators + swipe hint */}
                    <div className="flex flex-col items-center gap-3 mt-4">
                        <div className="flex gap-1.5">
                            {[0, 1, 2].map((idx) => (
                                <div key={idx} className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentStrategyIndex ? 'w-6 bg-emerald-500' : 'w-1.5 bg-slate-700'}`} />
                            ))}
                        </div>
                        <p className="text-slate-600 text-[10px] tracking-wide">Swipe to browse strategies</p>
                    </div>
                </div>
            </div>

            {/* Results Tracker Section */}
            <div id="results-tracker" className="container mx-auto px-6 py-8 md:py-10 scroll-animate">
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-12 md:mb-14">
                        <p className="text-emerald-400 text-xs uppercase tracking-[0.2em] font-medium mb-3">Bet Tracker</p>
                        <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
                            Track every bet,<br className="hidden sm:block" /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300">see the bigger picture</span>
                        </h2>
                        <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                            Value betting is a long-term game — variance will test your patience, and without data, it's impossible to know if your strategy is working. Our built-in results tracker gives you the clarity to stay the course and make smarter decisions over time.
                        </p>
                    </div>

                    {/* Two-column layout: copy left, features right */}
                    <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
                        {/* Left - Why tracking matters */}
                        <div className="flex-1 space-y-6">
                            <div className="space-y-4">
                                <h3 className="text-xl md:text-2xl font-semibold">
                                    If you want to be profitable long-term, tracking is non-negotiable
                                </h3>
                                <p className="text-slate-400 text-sm md:text-base leading-relaxed">
                                    The difference between recreational punters and profitable ones comes down to discipline and data. Knowing your true ROI, which bookmakers you perform best on, and how your strategy holds up over weeks and months is what separates long-term winners from everyone else — and more importantly, it holds you accountable to your process and staking.
                                </p>
                                <p className="text-slate-400 text-sm md:text-base leading-relaxed">
                                    We've built a custom in-house results tracker so you can log every bet, monitor your performance, and visualise your progress — all in one place. Whether you're just starting out or importing months of existing data, the tracker gives you everything you need to stay on top of your game.
                                </p>
                            </div>

                            {/* CTA */}
                            <div className="pt-2">
                                <button 
                                    onClick={() => {
                                        setSelectedPlanForRegister({ tier: 'FREE_TIER', display_name: 'Free Tier', monthly_price: 0, trial_days: 0 });
                                        setIsRegisterModalOpen(true);
                                    }}
                                    className="group text-emerald-400 hover:text-emerald-300 text-sm font-medium transition-colors duration-200"
                                >
                                    Start tracking for free
                                    <svg className="inline-block w-4 h-4 ml-1 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                </button>
                            </div>

                            {/* Tracker Screenshots Carousel */}
                            <div className="relative">
                                <div 
                                    ref={trackerScrollRef}
                                    className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 no-scrollbar rounded-xl"
                                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                                    onScroll={() => {
                                        if (trackerScrollRef.current) {
                                            const container = trackerScrollRef.current;
                                            const firstCard = container.children[0];
                                            if (firstCard) {
                                                const cardWidth = firstCard.offsetWidth + 12;
                                                const newIndex = Math.round(container.scrollLeft / cardWidth);
                                                setCurrentTrackerIndex(Math.min(newIndex, 3));
                                            }
                                        }
                                    }}
                                >
                                    {[
                                        { src: trackerOne, alt: 'Bet tracker overview' },
                                        { src: trackerTwo, alt: 'P/L performance chart' },
                                        { src: trackerThree, alt: 'Bookmaker breakdown' },
                                        { src: trackerFour, alt: 'Daily P/L breakdown' }
                                    ].map((img, idx) => (
                                        <div key={idx} className="flex-shrink-0 w-[85%] snap-center">
                                            <div className="rounded-lg overflow-hidden border border-white/[0.06] bg-white/[0.02]">
                                                <img src={img.src} alt={img.alt} className="w-full h-auto object-cover" />
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Arrow buttons */}
                                <button 
                                    onClick={() => {
                                        if (trackerScrollRef.current) {
                                            const container = trackerScrollRef.current;
                                            const firstCard = container.children[0];
                                            if (firstCard) {
                                                const cardWidth = firstCard.offsetWidth + 12;
                                                container.scrollBy({ left: -cardWidth, behavior: 'smooth' });
                                            }
                                        }
                                    }}
                                    className={`absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 transition-all duration-200 ${currentTrackerIndex === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100 hover:bg-black/80'}`}
                                    aria-label="Previous image"
                                >
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                <button 
                                    onClick={() => {
                                        if (trackerScrollRef.current) {
                                            const container = trackerScrollRef.current;
                                            const firstCard = container.children[0];
                                            if (firstCard) {
                                                const cardWidth = firstCard.offsetWidth + 12;
                                                container.scrollBy({ left: cardWidth, behavior: 'smooth' });
                                            }
                                        }
                                    }}
                                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 transition-all duration-200 ${currentTrackerIndex === 3 ? 'opacity-0 pointer-events-none' : 'opacity-100 hover:bg-black/80'}`}
                                    aria-label="Next image"
                                >
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>

                                {/* Dot indicators */}
                                <div className="flex justify-center gap-1.5 mt-3">
                                    {[0, 1, 2, 3].map((idx) => (
                                        <div key={idx} className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentTrackerIndex ? 'w-6 bg-emerald-500' : 'w-1.5 bg-slate-700'}`} />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right - Feature grid */}
                        {(() => {
                            const trackerFeatures = [
                                { title: 'Total P/L & ROI', desc: 'See your overall profit/loss and return on investment at a glance.', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" /> },
                                { title: 'P/L Performance', desc: 'Visualise your cumulative performance with interactive charts.', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /> },
                                { title: 'By Bookmaker', desc: 'Break down your results by bookmaker to see where you perform best.', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016A3.001 3.001 0 0021 9.349m-18 0a2.999 2.999 0 00.97-1.599L5.49 3h13.02l1.52 4.75A2.999 2.999 0 0021 9.349" /> },
                                { title: 'Daily & Monthly', desc: 'Drill into daily and monthly P/L breakdowns.', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" /> },
                                { title: 'Unit Sizing', desc: 'Custom unit settings so you can track bets your way.', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" /> },
                                { title: 'Bonus Bets', desc: 'Full support for bonus bets with accurate P/L calculations.', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" /> },
                                { title: 'Filter by Sport', desc: 'Isolate performance across different sports and leagues.', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z" /> },
                                { title: 'CSV Import', desc: 'Already have bet data? Import it and start analysing instantly.', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /> }
                            ];

                            // Group features into pairs for mobile carousel
                            const featurePairs = [];
                            for (let i = 0; i < trackerFeatures.length; i += 2) {
                                featurePairs.push(trackerFeatures.slice(i, i + 2));
                            }

                            const FeatureCard = ({ feature }) => (
                                <div className="rounded-xl p-4 md:p-5 border border-white/[0.04] bg-white/[0.02] hover:border-emerald-500/20 hover:bg-emerald-500/[0.02] transition-all duration-300 text-center">
                                    <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-3 mx-auto">
                                        <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            {feature.icon}
                                        </svg>
                                    </div>
                                    <h4 className="text-sm font-semibold mb-1">{feature.title}</h4>
                                    <p className="text-slate-500 text-xs leading-relaxed">{feature.desc}</p>
                                </div>
                            );

                            return (
                                <div className="flex-1 w-full">
                                    {/* Desktop grid */}
                                    <div className="hidden md:grid grid-cols-2 gap-4">
                                        {trackerFeatures.map((feature, idx) => (
                                            <FeatureCard key={idx} feature={feature} />
                                        ))}
                                    </div>

                                    {/* Mobile swipeable carousel - 2 cards per slide */}
                                    <div className="md:hidden">
                                        <div 
                                            className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 no-scrollbar" 
                                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                                            onScroll={(e) => {
                                                const container = e.target;
                                                const firstSlide = container.children[0];
                                                if (firstSlide) {
                                                    const slideWidth = firstSlide.offsetWidth + 12;
                                                    const newIndex = Math.round(container.scrollLeft / slideWidth);
                                                    setCurrentFeatureIndex(Math.min(newIndex, featurePairs.length - 1));
                                                }
                                            }}
                                        >
                                            {featurePairs.map((pair, pairIdx) => (
                                                <div key={pairIdx} className="flex-shrink-0 w-[90vw] max-w-[380px] snap-center grid grid-cols-2 gap-3">
                                                    {pair.map((feature, idx) => (
                                                        <FeatureCard key={idx} feature={feature} />
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex flex-col items-center gap-3 mt-3">
                                            <div className="flex gap-1.5">
                                                {featurePairs.map((_, idx) => (
                                                    <div key={idx} className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentFeatureIndex ? 'w-6 bg-emerald-500' : 'w-1.5 bg-slate-700'}`} />
                                                ))}
                                            </div>
                                            <p className="text-slate-600 text-[10px] tracking-wide">Swipe to see more features</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            </div>

            {/* Pricing Section */}
            <div id="pricing" className="container mx-auto px-6 py-8 md:py-10 scroll-animate">
                <div className="text-center mb-12">
                    <p className="text-emerald-400 text-xs uppercase tracking-[0.2em] font-medium mb-3">Pricing</p>
                    <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-3">
                        Simple, transparent pricing
                    </h2>
                    <p className="text-slate-400 text-base md:text-lg max-w-lg mx-auto mb-8">
                        Choose the plan that fits your strategy
                    </p>

                    {/* Sport Filter Chips */}
                    {!isLoadingPlans && subscriptionPlans.length > 0 && (
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                            {[...subscriptionPlans].sort((a, b) => {
                                const order = { 'NBA': 1, 'AFL': 2, 'ALL': 3 };
                                return (order[a.sport] || 999) - (order[b.sport] || 999);
                            }).map((sportCategory) => {
                                const visiblePlans = sportCategory.plans?.filter(plan => plan.show !== false) || [];
                                if (visiblePlans.length === 0) return null;

                                const getSportLogo = (sport) => {
                                    const logos = { 'NBA': nbaLogo, 'AFL': aflLogo, 'ALL': null };
                                    return logos[sport];
                                };

                                const isSelected = selectedSport === sportCategory.sport;

                                return (
                                    <button
                                        key={sportCategory.sport}
                                        onClick={() => setSelectedSport(sportCategory.sport)}
                                        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                                            isSelected
                                                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                                                : 'text-slate-400 hover:text-slate-200 border border-transparent hover:border-white/[0.06]'
                                        }`}
                                    >
                                        {getSportLogo(sportCategory.sport) ? (
                                            <div className={`w-5 h-5 rounded flex items-center justify-center p-0.5 ${isSelected ? '' : 'bg-white'}`}>
                                                <img src={getSportLogo(sportCategory.sport)} alt={`${sportCategory.sport} Logo`} className="w-full h-full object-contain" />
                                            </div>
                                        ) : (
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" />
                                            </svg>
                                        )}
                                        <span>{sportCategory.sport}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {isLoadingPlans ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent"></div>
                            <p className="mt-3 text-slate-500 text-sm">Loading plans...</p>
                        </div>
                    </div>
                ) : (
                    <div className="mt-10">
                        {subscriptionPlans.map((sportCategory) => {
                            if (sportCategory.sport !== selectedSport) return null;
                            if (!sportCategory.plans || sportCategory.plans.length === 0 || sportCategory.plans.every(p => p.show === false)) return null;

                            const visiblePlans = sportCategory.plans.filter(plan => plan.show !== false);
                            if (visiblePlans.length === 0) return null;

                            const totalCards = visiblePlans.length + 1;

                            // Handle scroll event to update dot indicators
                            const handleMobileScroll = () => {
                                if (mobileScrollRef.current) {
                                    const scrollContainer = mobileScrollRef.current;
                                    const cardWidth = scrollContainer.firstChild?.offsetWidth || 1;
                                    const scrollLeft = scrollContainer.scrollLeft;
                                    const newIndex = Math.round(scrollLeft / cardWidth);
                                    setCurrentMobilePlanIndex(Math.min(newIndex, totalCards - 1));
                                }
                            };
                            
                            return (
                                <div key={sportCategory.sport}>
                                    {/* Desktop Grid */}
                                    <div className={`hidden md:grid gap-6 max-w-5xl mx-auto px-4 md:px-0 ${visiblePlans.length === 1 ? 'md:grid-cols-1 max-w-md' : visiblePlans.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
                                        {visiblePlans.map((plan) => {
                                            const isPro = plan.tier === 'PRO';
                                            const isSoldOut = !plan.is_available;
                                            const hasLimitedSpots = !plan.unlimited && plan.max_spots && plan.spots_remaining > 0;
                                            const hasTrial = plan.trial_days > 0;

                                            return (
                                                <div 
                                                    key={plan.key}
                                                    className={`relative rounded-2xl p-6 sm:p-8 border transition-all duration-300 ${
                                                        isPro
                                                            ? 'bg-white/[0.03] border-emerald-500/30 shadow-[0_0_30px_rgba(16,185,129,0.08)]'
                                                            : 'bg-white/[0.02] border-white/[0.04] hover:border-white/[0.08]'
                                                    }`}
                                                >
                                                    {isPro && (
                                                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                                            <span className="bg-emerald-500 text-black px-3 py-1 rounded-full text-xs font-semibold">Most Popular</span>
                                                        </div>
                                                    )}
                                                    <div className="mb-6">
                                                        <h4 className="text-lg font-semibold mb-1">{plan.display_name || `${sportCategory.sport} ${plan.tier}`}</h4>
                                                        <div className="flex items-center justify-center gap-2 mb-4 flex-wrap min-h-[20px]">
                                                            {isSoldOut && <span className="bg-red-500/10 text-red-400 border border-red-500/20 px-2 py-0.5 rounded text-[11px] font-medium">Sold Out</span>}
                                                            {!isSoldOut && hasTrial && <span className="bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded text-[11px] font-medium">{plan.trial_days}-Day Trial</span>}
                                                            {!isSoldOut && hasLimitedSpots && <span className="bg-purple-500/10 text-purple-400 border border-purple-500/20 px-2 py-0.5 rounded text-[11px] font-medium">{plan.spots_remaining} spots left</span>}
                                                            {!isSoldOut && plan.unlimited && <span className="bg-purple-500/10 text-purple-400 border border-purple-500/20 px-2 py-0.5 rounded text-[11px] font-medium">Unlimited spots</span>}
                                                        </div>
                                                        {plan.monthly_price ? (
                                                            <div className="flex items-baseline justify-center gap-1">
                                                                <span className={`text-4xl font-bold ${isPro ? 'text-emerald-400' : ''}`}>${parseFloat(plan.monthly_price).toFixed(0)}</span>
                                                                <span className="text-slate-500 text-sm">/month</span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-2xl font-bold text-emerald-400">Lifetime Access</span>
                                                        )}
                                                    </div>
                                                    <ul className="space-y-2.5 mb-8">
                                                        {plan.subscription_features && plan.subscription_features.length > 0 ? (
                                                            plan.subscription_features.map((feature, featureIndex) => (
                                                                <li key={featureIndex} className="flex items-start text-sm">
                                                                    <svg className="w-4 h-4 text-emerald-400 mr-2.5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                                                    <span className="text-slate-400">{feature}</span>
                                                                </li>
                                                            ))
                                                        ) : (
                                                            [<li key="f1" className="flex items-start text-sm"><svg className="w-4 h-4 text-emerald-400 mr-2.5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg><span className="text-slate-400">{`Full access to ${sportCategory.sport} features`}</span></li>,
                                                            <li key="f2" className="flex items-start text-sm"><svg className="w-4 h-4 text-emerald-400 mr-2.5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg><span className="text-slate-400">Discord community access</span></li>,
                                                            <li key="f3" className="flex items-start text-sm"><svg className="w-4 h-4 text-emerald-400 mr-2.5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg><span className="text-slate-400">Automated betting alerts</span></li>]
                                                        )}
                                                    </ul>
                                                    <button 
                                                        onClick={() => { setSelectedPlanForRegister({ ...plan, sport: sportCategory.sport }); setIsRegisterModalOpen(true); }}
                                                        disabled={isSoldOut || plan.can_purchase === false}
                                                        className={`w-full py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                                                            isSoldOut || plan.can_purchase === false
                                                                ? 'bg-white/[0.03] text-slate-600 cursor-not-allowed border border-white/[0.04]'
                                                                : isPro
                                                                ? 'bg-emerald-500 hover:bg-emerald-400 text-black shadow-[0_0_15px_rgba(16,185,129,0.2)]'
                                                                : 'bg-white/[0.06] hover:bg-white/[0.1] text-white border border-white/[0.06]'
                                                        }`}
                                                    >
                                                        {isSoldOut ? 'Sold Out' : plan.can_purchase === false ? 'Coming Soon' : 'Get Started'}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Mobile Swipeable Carousel */}
                                    <div className="md:hidden">
                                        <div 
                                            ref={mobileScrollRef}
                                            onScroll={handleMobileScroll}
                                            className="flex gap-4 overflow-x-auto snap-x snap-mandatory pt-4 pb-4 px-4 no-scrollbar"
                                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                                        >
                                            {visiblePlans.map((plan) => {
                                                const isPro = plan.tier === 'PRO';
                                                const isSoldOut = !plan.is_available;
                                                const hasLimitedSpots = !plan.unlimited && plan.max_spots && plan.spots_remaining > 0;
                                                const hasTrial = plan.trial_days > 0;

                                                return (
                                                    <div 
                                                        key={plan.key}
                                                        className={`flex-shrink-0 w-[85vw] max-w-[340px] snap-center relative rounded-2xl p-6 border transition-all duration-300 ${
                                                            isPro
                                                                ? 'bg-white/[0.03] border-emerald-500/30 shadow-[0_0_30px_rgba(16,185,129,0.08)]'
                                                                : 'bg-white/[0.02] border-white/[0.04]'
                                                        }`}
                                                    >
                                                        {isPro && (
                                                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                                                <span className="bg-emerald-500 text-black px-3 py-1 rounded-full text-xs font-semibold">Most Popular</span>
                                                            </div>
                                                        )}
                                                        <div className="mb-6">
                                                            <h4 className="text-lg font-semibold mb-1">{plan.display_name || `${sportCategory.sport} ${plan.tier}`}</h4>
                                                            <div className="flex items-center justify-center gap-2 mb-4 flex-wrap min-h-[20px]">
                                                                {isSoldOut && <span className="bg-red-500/10 text-red-400 border border-red-500/20 px-2 py-0.5 rounded text-[11px] font-medium">Sold Out</span>}
                                                                {!isSoldOut && hasTrial && <span className="bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded text-[11px] font-medium">{plan.trial_days}-Day Trial</span>}
                                                                {!isSoldOut && hasLimitedSpots && <span className="bg-purple-500/10 text-purple-400 border border-purple-500/20 px-2 py-0.5 rounded text-[11px] font-medium">{plan.spots_remaining} spots left</span>}
                                                                {!isSoldOut && plan.unlimited && <span className="bg-purple-500/10 text-purple-400 border border-purple-500/20 px-2 py-0.5 rounded text-[11px] font-medium">Unlimited spots</span>}
                                                            </div>
                                                            {plan.monthly_price ? (
                                                                <div className="flex items-baseline justify-center gap-1">
                                                                    <span className={`text-4xl font-bold ${isPro ? 'text-emerald-400' : ''}`}>${parseFloat(plan.monthly_price).toFixed(0)}</span>
                                                                    <span className="text-slate-500 text-sm">/month</span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-2xl font-bold text-emerald-400">Lifetime Access</span>
                                                            )}
                                                        </div>
                                                        <ul className="space-y-2.5 mb-8">
                                                            {plan.subscription_features && plan.subscription_features.length > 0 ? (
                                                                plan.subscription_features.map((feature, featureIndex) => (
                                                                    <li key={featureIndex} className="flex items-start text-sm">
                                                                        <svg className="w-4 h-4 text-emerald-400 mr-2.5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                                                        <span className="text-slate-400">{feature}</span>
                                                                    </li>
                                                                ))
                                                            ) : (
                                                                [`Full access to ${sportCategory.sport} features`, 'Discord community access', 'Automated betting alerts'].map((f, i) => (
                                                                    <li key={i} className="flex items-start text-sm">
                                                                        <svg className="w-4 h-4 text-emerald-400 mr-2.5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                                                        <span className="text-slate-400">{f}</span>
                                                                    </li>
                                                                ))
                                                            )}
                                                        </ul>
                                                        <button 
                                                            onClick={() => { setSelectedPlanForRegister({ ...plan, sport: sportCategory.sport }); setIsRegisterModalOpen(true); }}
                                                            disabled={isSoldOut || plan.can_purchase === false}
                                                            className={`w-full py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                                                                isSoldOut || plan.can_purchase === false
                                                                    ? 'bg-white/[0.03] text-slate-600 cursor-not-allowed border border-white/[0.04]'
                                                                    : isPro
                                                                    ? 'bg-emerald-500 hover:bg-emerald-400 text-black shadow-[0_0_15px_rgba(16,185,129,0.2)]'
                                                                    : 'bg-white/[0.06] hover:bg-white/[0.1] text-white border border-white/[0.06]'
                                                            }`}
                                                        >
                                                            {isSoldOut ? 'Sold Out' : plan.can_purchase === false ? 'Coming Soon' : 'Get Started'}
                                                        </button>
                                                    </div>
                                                );
                                            })}

                                            {/* FREE Tier Card - Mobile */}
                                            <div className="flex-shrink-0 w-[85vw] max-w-[340px] snap-center relative rounded-2xl p-6 border transition-all duration-300 bg-white/[0.02] border-white/[0.04]">
                                                <div className="mb-6">
                                                    <h4 className="text-lg font-semibold mb-1">Free Tier</h4>
                                                    <div className="flex items-center justify-center gap-2 mb-4 min-h-[20px]">
                                                        <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded text-[11px] font-medium">No Payment Required</span>
                                                    </div>
                                                    <div className="flex items-baseline justify-center gap-1">
                                                        <span className="text-4xl font-bold text-emerald-400">$0</span>
                                                        <span className="text-slate-500 text-sm">/forever</span>
                                                    </div>
                                                </div>
                                                <ul className="space-y-2.5 mb-8">
                                                    {['Results Tracker', 'Bet Performance Analytics', 'Import Your Bet Data'].map((f, i) => (
                                                        <li key={i} className="flex items-start text-sm">
                                                            <svg className="w-4 h-4 text-emerald-400 mr-2.5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                                            <span className="text-slate-400">{f}</span>
                                                        </li>
                                                    ))}
                                                    {['Discord Community', 'Automated Alerts'].map((f, i) => (
                                                        <li key={i + 3} className="flex items-start text-sm">
                                                            <svg className="w-4 h-4 text-slate-600 mr-2.5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                                            <span className="text-slate-600">{f}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                                <button 
                                                    onClick={() => { setSelectedPlanForRegister({ tier: 'FREE_TIER', display_name: 'Free Tier', monthly_price: 0, trial_days: 0 }); setIsRegisterModalOpen(true); }}
                                                    className="w-full py-2.5 rounded-lg text-sm font-medium bg-white/[0.06] hover:bg-white/[0.1] text-white border border-white/[0.06] transition-all duration-200"
                                                >
                                                    Get Started Free
                                                </button>
                                            </div>
                                        </div>

                                        {/* Swipe hint + dot indicators */}
                                        <div className="flex flex-col items-center gap-3 mt-4">
                                            <div className="flex gap-1.5">
                                                {Array.from({ length: totalCards }).map((_, idx) => (
                                                    <div key={idx} className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentMobilePlanIndex ? 'w-6 bg-emerald-500' : 'w-1.5 bg-slate-700'}`} />
                                                ))}
                                            </div>
                                            <p className="text-slate-600 text-[10px] tracking-wide">Swipe to browse plans</p>
                                        </div>
                                    </div>

                                    {/* FREE Tier - Desktop */}
                                    <div className="hidden md:block mt-10">
                                        <div className="text-center mb-4">
                                            <p className="text-slate-500 text-xs uppercase tracking-wider">Or start for free</p>
                                        </div>
                                        <div className="max-w-sm mx-auto">
                                            <div className="rounded-2xl p-6 border border-white/[0.04] bg-white/[0.02] text-center">
                                                <h4 className="text-lg font-semibold mb-1">Free Tier</h4>
                                                <div className="flex items-baseline justify-center gap-1 mb-4">
                                                    <span className="text-3xl font-bold text-emerald-400">$0</span>
                                                    <span className="text-slate-500 text-sm">/forever</span>
                                                </div>
                                                <p className="text-slate-400 text-sm mb-5">Results Tracker, Analytics & CSV Import</p>
                                                <button 
                                                    onClick={() => {
                                                        setSelectedPlanForRegister({ tier: 'FREE_TIER', display_name: 'Free Tier', monthly_price: 0, trial_days: 0 });
                                                        setIsRegisterModalOpen(true);
                                                    }}
                                                    className="w-full py-2.5 rounded-lg text-sm font-medium bg-white/[0.06] hover:bg-white/[0.1] text-white border border-white/[0.06] transition-all duration-200"
                                                >
                                                    Get Started Free
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* FAQ Section */}
            <div id="faq" className="container mx-auto px-6 py-8 md:py-10 scroll-animate">
                <div className="text-center mb-12">
                    <p className="text-emerald-400 text-xs uppercase tracking-[0.2em] font-medium mb-3">FAQ</p>
                    <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
                        Frequently asked questions
                    </h2>
                </div>

                <div className="max-w-2xl mx-auto space-y-2">
                    {[
                        { q: 'What is positive EV betting?', a: 'Positive Expected Value (EV) betting is when the odds you receive on a bet are better than the true probability of that outcome occurring. Over time, consistently betting on positive EV opportunities leads to long-term profitability.' },
                        { q: 'Will I win every Positive EV or Value bet?', a: 'No, positive EV betting does not make your bet any more likely to win. It just means the bets have a greater chance of happening than what the odds suggest. Losses and negative variance are inevitable. Positive EV is a long-term strategy designed to give you the edge across a large sample size of bets.' },
                        { q: 'How does arbitrage betting work?', a: 'Arbitrage betting involves placing bets on all possible outcomes of an event across different sportsbooks where the combined odds guarantee a profit regardless of the result. Our platform scans for these opportunities every minute and alerts you instantly.' },
                        { q: 'Which sportsbooks do you support?', a: 'We support all major Australian sportsbooks including Sportsbet, TAB, Pointsbet, Dabble, Neds, Betright, Tabtouch, and more.' },
                        { q: 'Can I cancel my subscription anytime?', a: 'Yes, you can cancel your subscription at any time. There are no long-term contracts or cancellation fees. You\'ll continue to have access until the end of your current billing period.' },
                        { q: 'Do you offer a free trial?', a: 'We offer a 7-day free trial for the BASIC tier so you can test our platform and see the value for yourself before committing to a subscription.' },
                        { q: 'How fast are the odds updated?', a: 'Our system scans and updates odds every minute across all supported bookmakers, ensuring you receive timely alerts for all opportunities.' }
                    ].map((item, index) => (
                        <div key={index} className="border-b border-white/[0.04] last:border-0">
                            <button 
                                onClick={() => toggleFaqItem(index)}
                                className="w-full py-5 text-left flex items-center justify-between focus:outline-none group"
                            >
                                <span className="text-[15px] font-medium text-slate-200 group-hover:text-white transition-colors pr-4">{item.q}</span>
                                <svg 
                                    className={`w-4 h-4 text-slate-500 transition-transform duration-200 flex-shrink-0 ${expandedFaqItems.includes(index) ? 'rotate-45' : ''}`}
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                            </button>
                            {expandedFaqItems.includes(index) && (
                                <div className="pb-5">
                                    <p className="text-slate-400 text-sm leading-relaxed">{item.a}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Final CTA Section */}
            <div className="container mx-auto px-6 pb-8 scroll-animate">
                <div className="max-w-2xl mx-auto text-center">
                    <div className="relative rounded-2xl p-8 sm:p-12 overflow-hidden">
                        {/* Subtle gradient border effect */}
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-emerald-500/[0.08] to-transparent"></div>
                        <div className="absolute inset-[1px] rounded-2xl bg-[#020817]"></div>
                        <div className="relative">
                            <h2 className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight">
                                Ready to get an edge?
                            </h2>
                            <p className="text-slate-400 text-base mb-8 max-w-md mx-auto">
                                Join likeminded punters already using SnipeAIO. Start your free trial today.
                            </p>
                            
                            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
                                <button 
                                    onClick={() => {
                                        const nbaSport = subscriptionPlans.find(s => s.sport === 'NBA');
                                        const basicPlan = nbaSport?.plans?.find(p => p.tier === 'BASIC' && p.show !== false);
                                        if (basicPlan) {
                                            setSelectedPlanForRegister({ ...basicPlan, sport: 'NBA' });
                                            setIsRegisterModalOpen(true);
                                        }
                                    }}
                                    className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-8 py-3 rounded-xl text-sm transition-all duration-200 shadow-[0_0_20px_rgba(16,185,129,0.25)]"
                                >
                                    Start free trial
                                </button>
                                <button 
                                    onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                                    className="bg-white/[0.06] hover:bg-white/[0.1] px-8 py-3 rounded-xl text-sm font-medium transition-all duration-200 border border-white/[0.06]"
                                >
                                    See plans
                                </button>
                            </div>
                            
                            <p className="text-slate-600 text-[11px] uppercase tracking-wider">
                                7-day basic trial <span className="mx-1.5">·</span> Card required <span className="mx-1.5">·</span> 1 trial per customer
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="border-t border-white/[0.04]">
                <div className="container mx-auto px-6 py-12">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                        {/* Brand */}
                        <div className="text-center md:text-left">
                            <img src={snipeLogo} alt="Snipe Logo" className="h-10 w-auto mb-3 mx-auto md:mx-0" />
                            <p className="text-slate-500 text-sm leading-relaxed">
                                Need help? Contact us at{' '}
                                <a href="mailto:info@snipeaio.com" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                                    info@snipeaio.com
                                </a>
                            </p>
                        </div>

                        {/* Product */}
                        <div className="text-center md:text-left">
                            <h4 className="text-white/60 text-xs uppercase tracking-wider font-medium mb-3">Product</h4>
                            <ul className="space-y-1.5">
                                {[
                                    { href: '#how-it-works', label: 'Getting started' },
                                    { href: '#strategies', label: 'How it works' },
                                    { href: '#results-tracker', label: 'Bet Tracker' },
                                    { href: '#pricing', label: 'Pricing' },
                                    { href: '#faq', label: 'FAQ' }
                                ].map((link) => (
                                    <li key={link.href}>
                                        <a href={link.href} className="text-slate-500 hover:text-slate-300 transition-colors duration-200 text-sm">{link.label}</a>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Legal */}
                        <div className="text-center md:text-left">
                            <h4 className="text-white/60 text-xs uppercase tracking-wider font-medium mb-3">Legal</h4>
                            <ul className="space-y-1.5">
                                {[
                                    { href: '/privacy', label: 'Privacy Policy' },
                                    { href: '/terms', label: 'Terms of Service' },
                                    { href: '/disclaimer', label: 'Disclaimer' },
                                    { href: '/responsible-gambling', label: 'Responsible Gambling' }
                                ].map((link) => (
                                    <li key={link.href}>
                                        <a href={link.href} className="text-slate-500 hover:text-slate-300 transition-colors duration-200 text-sm">{link.label}</a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Bottom Bar */}
                    <div className="pt-8 border-t border-white/[0.04] flex flex-col md:flex-row justify-between items-center">
                        <p className="text-slate-600 text-xs mb-4 md:mb-0">
                            © 2026 SnipeAIO. All rights reserved.
                        </p>
                        
                        {/* Social Links */}
                        <div className="flex space-x-5">
                            {[
                                { href: '#twitter', icon: 'M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84' },
                                { href: '#discord', icon: 'M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z' },
                                { href: '#instagram', icon: 'M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z' },
                                { href: '#youtube', icon: 'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z' }
                            ].map((social) => (
                                <a key={social.href} href={social.href} className="text-slate-600 hover:text-slate-400 transition-colors duration-200">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d={social.icon} />
                                    </svg>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </footer>

            {/* CSS for animations */}
            <style jsx>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll {
          animation: scroll 20s linear infinite;
        }
        .animate-scroll:hover {
          animation-play-state: paused;
        }
        
        .scroll-animate {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
          animation: fallbackFadeIn 0.6s ease-out 2s forwards;
        }
        .scroll-animate.animate-in {
          opacity: 1;
          transform: translateY(0);
          animation: none;
        }
        
        @keyframes fallbackFadeIn {
          to { opacity: 1; transform: translateY(0); }
        }
        
        @media (prefers-reduced-motion: reduce) {
          .scroll-animate {
            opacity: 1;
            transform: translateY(0);
            transition: none;
            animation: none;
          }
        }
        
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
            </div>

            {/* Registration Success Modal */}
            {showRegistrationSuccess && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>

                    {/* Modal Content */}
                    <div className="relative w-full max-w-md bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl sm:rounded-2xl border border-emerald-500/50 shadow-2xl p-5 sm:p-8">
                        <div className="text-center">
                            {/* Success Icon */}
                            <div className="flex justify-center mb-4 sm:mb-6">
                                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                                    <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            </div>

                            {/* Success Message */}
                            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
                                {isFreeTierRegistration ? 'Account Created!' : 'Payment Successful!'}
                            </h2>
                            <p className="text-slate-400 text-sm sm:text-base mb-4">
                                {isFreeTierRegistration ? 'Redirecting to your dashboard...' : 'Setting up your account...'}
                            </p>

                            {/* Loading Spinner */}
                            <div className="flex justify-center">
                                <div className="animate-spin rounded-full h-7 w-7 sm:h-8 sm:w-8 border-4 border-emerald-500 border-t-transparent"></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Processing Registration Modal */}
            {isProcessingRegistration && !showRegistrationSuccess && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>

                    {/* Modal Content */}
                    <div className="relative w-full max-w-md bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl sm:rounded-2xl border border-slate-800/50 shadow-2xl p-5 sm:p-8">
                        <div className="text-center">
                            <h2 className="text-lg sm:text-xl font-bold text-white mb-4">Processing...</h2>
                            <div className="flex justify-center">
                                <div className="animate-spin rounded-full h-7 w-7 sm:h-8 sm:w-8 border-4 border-emerald-500 border-t-transparent"></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Login Modal */}
            <LoginModal 
                isOpen={isLoginModalOpen} 
                onClose={() => {
                    setIsLoginModalOpen(false);
                    setRegistrationEmail('');
                }}
                prefillEmail={registrationEmail}
            />

            {/* Register Modal */}
            <RegisterModal 
                isOpen={isRegisterModalOpen} 
                onClose={() => {
                    setIsRegisterModalOpen(false);
                    setSelectedPlanForRegister(null);
                }} 
                selectedPlan={selectedPlanForRegister}
                onSwitchToLogin={() => setIsLoginModalOpen(true)}
                onFreeTierSuccess={(token, user) => {
                    // Show success message briefly then redirect
                    setIsFreeTierRegistration(true);
                    setShowRegistrationSuccess(true);
                    setTimeout(() => {
                        setShowRegistrationSuccess(false);
                        setIsFreeTierRegistration(false);
                        // Navigate to dashboard (full page reload to refresh auth state)
                        window.location.href = '/discord';
                    }, 1500);
                }}
            />
        </div>
    );
}

export default LandingPage;